import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useAudio } from './AudioContext'
import { useShortChapter } from './ShortChapterContext'
import { ShortChapterReadingContent } from './ShortChapterReadingContent'
import { GET_SHORT_CHAPTER, GET_SHORT_CHAPTER_ARTICLES } from './api'

type Props = {
  slug: string | null
  onClose: () => void
}

export function ShortChapterOverlay({ slug, onClose }: Props) {
  const [isVisible, setIsVisible] = useState(false)
  const { setIsReadingOverlayOpen, isPlaying, setShowMiniPlayer, setOnMiniPlayerClick } = useAudio()
  const { openShortChapter } = useShortChapter()

  const { data: chapterData, error: chapterError } = useQuery(GET_SHORT_CHAPTER, {
    variables: { slug: slug ?? '' },
    skip: !slug,
    fetchPolicy: 'cache-first',
  })

  const { data: articleData, error: articleError } = useQuery(GET_SHORT_CHAPTER_ARTICLES, {
    variables: { slug: slug ?? '' },
    skip: !slug,
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (slug) {
      setIsReadingOverlayOpen(true)
      setShowMiniPlayer(false)
      // Solange das Kapitel offen liegt, gibt es nichts wieder zu öffnen. Der Rückruf
      // von vorhin zeigte auf das zuletzt geschlossene Kapitel - bliebe er stehen,
      // führte ein Tipp auf den Mini-Player später ins falsche.
      setOnMiniPlayerClick(null)
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)))
    }
  }, [slug, setIsReadingOverlayOpen, setShowMiniPlayer, setOnMiniPlayerClick])

  const handleClose = () => {
    const wasPlaying = isPlaying
    const closingSlug = slug
    setIsVisible(false)
    setTimeout(() => {
      setIsReadingOverlayOpen(false)
      onClose()
      // Läuft das Audio weiter, übernimmt der Mini-Player; ein Tipp darauf öffnet
      // das Kurzkapitel wieder.
      if (wasPlaying && closingSlug) {
        setShowMiniPlayer(true)
        setOnMiniPlayerClick(() => () => openShortChapter(closingSlug))
      }
    }, 300)
  }

  if (!slug) return null

  // Ohne Inhalt bliebe die eingeblendete Fläche leer - dann lieber ein kurzer Hinweis
  // samt Schließen-Knopf, den die Leseansicht selbst mitbringt.
  if (chapterError || articleError) {
    return (
      <ShortChapterReadingContent
        chapter={{
          title: 'Kapitel nicht verfügbar',
          summary: 'Das Kapitel konnte nicht geladen werden.',
        }}
        articles={[]}
        chapterSlug={slug}
        isOpen={isVisible}
        onClose={handleClose}
      />
    )
  }

  const chapter = chapterData?.chapters?.[0] ?? {
    title: '',
    summary: null,
    hook: null,
    takeaway: null,
    duration: null,
    cover: null,
  }
  const articles = articleData?.chapters?.[0]?.articles ?? []

  return (
    <ShortChapterReadingContent
      chapter={chapter}
      articles={articles}
      chapterSlug={slug}
      isOpen={isVisible}
      onClose={handleClose}
    />
  )
}
